/**
 * h5/src/ws/heartbeat.ts
 * 心跳调度器
 *
 * 协议（对齐 E.0 §6.5）：
 * - 每 30s 调用 send() 发出 heartbeat 帧
 * - 服务端最多 60s 节流响应 heartbeat_ack（或 pong）
 * - 连续 3 次（90s）无响应 → isAlive() = false → 触发 RECONNECTING
 *
 * 设计：
 * - 所有 timer / Date.now 可通过 opts 注入（测试隔离）
 * - 多次 start() 幂等（自动 stop 前一个）
 */

import type { HeartbeatOptions } from './types'

/**
 * 心跳调度器接口
 */
export interface HeartbeatScheduler {
  /**
   * 启动心跳（幂等：若已启动则先 stop 再重启）
   * @param sendFn 发送心跳帧的回调
   */
  start(sendFn: () => void): void
  /**
   * 收到 heartbeat_ack / pong 时调用，重置超时计数
   */
  notifyAck(): void
  /**
   * 停止心跳，清理 timer
   */
  stop(): void
  /**
   * 当前是否"存活"：距上次 ack < timeoutMs
   */
  isAlive(): boolean
  /**
   * 上次收到 ack 的时间戳（ms），0 表示尚未收到过
   */
  readonly lastAckAt: number
}

/**
 * 创建心跳调度器
 *
 * @example
 * ```ts
 * const hb = createHeartbeat()
 * hb.start(() => ws.send('{"type":"heartbeat"}'))
 * // 收到服务端 pong：
 * hb.notifyAck()
 * // 检查是否存活：
 * if (!hb.isAlive()) { reconnect() }
 * // 断开时：
 * hb.stop()
 * ```
 */
export function createHeartbeat(opts?: HeartbeatOptions): HeartbeatScheduler {
  const intervalMs = opts?.intervalMs ?? 30_000
  const timeoutMs = opts?.timeoutMs ?? 90_000
  const setIntervalFn = opts?.setIntervalFn ?? setInterval
  const clearIntervalFn = opts?.clearIntervalFn ?? clearInterval
  const nowFn = opts?.nowFn ?? Date.now

  let _timerId: ReturnType<typeof setInterval> | null = null
  let _lastAckAt = 0
  let _startedAt = 0

  return {
    get lastAckAt() {
      return _lastAckAt
    },

    /**
     * 启动心跳定时器（幂等）
     */
    start(send: () => void): void {
      // 如果已有定时器，先停止（幂等）
      if (_timerId !== null) {
        clearIntervalFn(_timerId)
        _timerId = null
      }

      _startedAt = nowFn()
      // 初始化 lastAckAt 为启动时间，避免启动后立即判定超时
      _lastAckAt = _startedAt

      _timerId = setIntervalFn(() => {
        send()
      }, intervalMs)
    },

    /**
     * 收到 heartbeat_ack / pong 时重置计时
     */
    notifyAck(): void {
      _lastAckAt = nowFn()
    },

    /**
     * 停止心跳
     */
    stop(): void {
      if (_timerId !== null) {
        clearIntervalFn(_timerId)
        _timerId = null
      }
    },

    /**
     * 判定是否存活（距上次 ack < timeoutMs）
     * 未启动时（_lastAckAt === 0）返回 true（避免误判）
     */
    isAlive(): boolean {
      if (_lastAckAt === 0) return true
      return nowFn() - _lastAckAt < timeoutMs
    },
  }
}
